/**
 * US phone input for the Team and Settings tabs (docs/plans/oncall-line-plan.md
 * 6.2, 7.1, 7.7; WP-11).
 *
 * The line and every member are US (NANP) numbers stored as E.164 (plan 16,
 * WP-06: "The line and every member must be NANP E.164; 900 refused"). The
 * server normalizes and judges every number (services/oncall/line.js
 * normalizeNanp, utils/phone.js toE164) and answers 422 ONCALL_INVALID for
 * one it refuses; this reader only lets the form say so before a request,
 * and sends the number already in E.164. It accepts what people type:
 * "(555) 234-5678", "555.234.5678", "1 555 234 5678", "+1 555 234 5678".
 */

/** +1, an area code and an exchange each starting 2 to 9 (line.js NANP_RE). */
const NANP_E164 = /^\+1[2-9]\d{2}[2-9]\d{6}$/;

/** Characters people type between digits; anything else makes the entry unreadable. */
const SEPARATORS = /[\s().\-/]/g;

/** A typed US number as E.164 ("+15552345678"), or null when it is not one. */
export function toUsE164(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const text = raw.trim();
  if (!text || text.length > 40) return null;
  const compact = text.replace(SEPARATORS, '');
  let digits: string;
  if (compact.startsWith('+')) {
    if (!/^\+\d+$/.test(compact)) return null;
    digits = compact.slice(1);
    if (!digits.startsWith('1')) return null;
  } else {
    if (!/^\d+$/.test(compact)) return null;
    digits = compact;
  }
  if (digits.length === 10) digits = `1${digits}`;
  if (digits.length !== 11 || !digits.startsWith('1')) return null;
  const e164 = `+${digits}`;
  return NANP_E164.test(e164) ? e164 : null;
}

/** 900 numbers are premium-rate (plan 6.2): never a line or a member. */
export function isPremiumUs(e164: string | null | undefined): boolean {
  return typeof e164 === 'string' && e164.startsWith('+1900');
}

/**
 * Why a typed number cannot be used, or null when it can. `lineNumber` is the
 * line's own number: a member may not be it (the hunt would dial the line
 * from the line; plan 16, WP-06).
 */
export function usNumberProblem(raw: string, { lineNumber }: { lineNumber?: string | null } = {}): string | null {
  if (!raw.trim()) return 'Enter a phone number.';
  const e164 = toUsE164(raw);
  if (!e164) return 'Enter a US number: ten digits with the area code, for example (555) 234-5678.';
  if (isPremiumUs(e164)) return 'A 900 number cannot be used.';
  if (lineNumber && e164 === lineNumber) return "This is the line's own number. Enter the person's cell.";
  return null;
}
